import {
  Discount,
  DiscountRule1Props,
  QuotationSchema,
  QuotationUpdateSchema,
  QuotationWithDraftsSchema,
} from "../types";
import { Link } from "react-router-dom";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import { WorksiteSimpleSchema, UserWithRoleSchema } from "../../types";
import {
  generateFilter,
  splitStringWithLimit,
} from "@feature/users/connections";
import { UserReducedSchema } from "@feature/types";
import { checkExternalUser } from "../../../utils/checkExternalUser";
import sharedApiRequest from "../../../services/apiRequest";
import { validateEmail } from "../../../utils/validate-email";
import { UserOption } from "../../commons";
import moment from "moment";
import { debug } from "console";

const applyPercentageDiscount = (value: number, discount: number) =>
  value * (1 + discount / 100);

export const computeDiscountedValue = (
  price: number,
  discount: Discount,
  quantity?: number,
  unitPrice?: number
) => {
  if (Boolean(quantity) !== Boolean(unitPrice)) {
    // throw new Error('Quantity and unit price must be both defined or both undefined');
    console.error(
      "Quantity and unit price must be both defined or both undefined"
    );
    return price * (quantity || 0);
  }

  let initialPrice: number = !!quantity ? quantity * unitPrice! : price;

  const { type, value } = discount;
  switch (type) {
    case "absolute":
      return value as number;
    case "absolute_percentage":
      // DEPRECATED
      return applyPercentageDiscount(initialPrice, -(value as number));
    case "discount_rule_1":
      const {
        discount_1,
        discount_2,
        discount_3,
        marginality,
        transport_eur_kg,
      } = value as DiscountRule1Props;
      return (
        applyPercentageDiscount(
          applyPercentageDiscount(
            applyPercentageDiscount(
              applyPercentageDiscount(initialPrice, -(discount_1 ?? 0)),
              -(discount_2 ?? 0)
            ),
            -(discount_3 ?? 0)
          ),
          +(marginality ?? 0)
        ) +
        (transport_eur_kg ?? 0) * (quantity || 0)
      );
    default:
    case "no_discount":
      return initialPrice;
  }
};

export const dateToString = (date: string) => {
  return moment(date).format("DD/MM/YYYY");
};

export const getWorksiteInfo = (
  countryCode: string,
  worksite?: WorksiteSimpleSchema,
  showLink: boolean = true
) => {
  if (!worksite) return null;

  const {
    id,
    address,
    zip_code,
    cod_province,
    des_province,
    cod_worksite_type,
    cod_worksite_construction,
    estime_date_start,
    estime_date_end,
    des_worksite,
    city,
  } = worksite;

  // HOTFIX H22927252507: address different from des_worksite

  function getWorksiteName() {
    const addressIsEqualToDes =
      des_worksite.trim().toUpperCase() ===
      `${address}  - ${city}`.trim().toUpperCase();

    return (
      (!addressIsEqualToDes ? des_worksite + " " + address : des_worksite) +
      ` - ${zip_code} - ${des_province} (${cod_province})`
    );
  }

  return [
    {
      key: "worksite",
      value: getWorksiteName(),
      icon: showLink ? (
        <Link
          to={`/${countryCode}/worksites/${id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ArrowTopRightOnSquareIcon className="inline w-4 -mt-0.5 text-kerakoll-700" />
        </Link>
      ) : null,
    },
    { key: "worksite_type", value: cod_worksite_type },
    { key: "worksite_construction", value: cod_worksite_construction },
    {
      key: "worksite_estime_dates",
      value:
        estime_date_start && estime_date_end
          ? `${dateToString(estime_date_start)} - ${dateToString(
              estime_date_end
            )}`
          : "N/D",
    },
  ];
};

export const fetchAgentEnterprises = async (
  apiRequest: any,
  agentId: number,
  name?: string,
  limit: number = 10,
  alsoContactUsers: boolean = true
): Promise<{ items: UserWithRoleSchema[]; total: number }> => {
  const splitSearch = name ? splitStringWithLimit(name, " ", 2) : undefined;
  const payload = {
    country_code: "it",
    perm: `user_${agentId}`,
    filters: {
      logic: "and",
      filters: [
        ...(!!name
          ? [
              {
                logic: "or",
                filters: [
                  generateFilter("email", name),
                  generateFilter("company", name),
                  ...(!!name
                    ? splitSearch.length < 2
                      ? ["firstname", "lastname"].map((x) =>
                          generateFilter(x, name)
                        )
                      : [
                          ["firstname", "lastname"],
                          ["lastname", "firstname"],
                        ].map((columns) => ({
                          logic: "and",
                          filters: _.zip(columns, splitSearch).map(
                            ([column, part]) => generateFilter(column!, part!)
                          ),
                        }))
                    : []),
                ],
              },
            ]
          : []),
        ...(alsoContactUsers ? [
          {
            logic: "or",
            filters: [
              {
                value: ["enterprise"],
                operator: "in",
                column: "role.name",
              },
              {
                value: ["enterprise"],
                operator: "in",
                column: "name",
                model: "account_user_role",
              },
            ],
          }
        ] : [
          {
            value: ["enterprise"],
            operator: "in",
            column: "role.name",
          }
        ])
      ],
    },
    limit,
    offset: 0,
  };
  return await apiRequest.post("/connections", payload);
};

export const fetchAgentResales = async (
  apiRequest: any,
  agentId?: number,
  name?: string,
  limit: number = 10,
  role?: string,
  onlyActiveUsers: boolean = false,
  alsoContactUsers: boolean = true
): Promise<{ items: UserWithRoleSchema[]; total: number }> => {

  if (!agentId && !role) throw new Error("Missing agentId or role");
  const splitSearch = name ? splitStringWithLimit(name, " ", 2) : undefined;
  const payload = {
    country_code: "it",
    perm: agentId ? `user_${agentId}` : role,
    filters: {
      logic: "and",
      filters: [
        ...(!!name
          ? [
              {
                logic: "or",
                filters: [
                  generateFilter("email", name),
                  generateFilter("company", name),
                  ...(!!name
                    ? splitSearch.length < 2
                      ? ["firstname", "lastname"].map((x) =>
                          generateFilter(x, name)
                        )
                      : [
                          ["firstname", "lastname"],
                          ["lastname", "firstname"],
                        ].map((columns) => ({
                          logic: "and",
                          filters: _.zip(columns, splitSearch).map(
                            ([column, part]) => generateFilter(column!, part!)
                          ),
                        }))
                    : []),
                ],
              },
            ]
          : []),
        ...(alsoContactUsers ? [
          {
            logic: "or",
            filters: [
              {
                value: ["resale", "resale_tintometer"],
                operator: "in",
                column: "role.name",
              },
              {
                value: ["resale", "resale_tintometer"],
                operator: "in",
                column: "name",
                model: "account_user_role",
              },
            ],
          }
        ] : [
          {
            value: ["resale", "resale_tintometer"],
            operator: "in",
            column: "role.name",
          }
        ]),
        ...(onlyActiveUsers
          ? [
              {
                value: true,
                operator: "=",
                column: "flg_active",
              },
            ]
          : []),
        {
          column: "email",
          operator: "is_not_null",
          value: null,
        }
      ],
    },
    ordering: null,
    limit,
    offset: 0,
  };
  return await apiRequest.post("/connections", payload);
};

export const mapResalesOptions = (resales: UserWithRoleSchema[]) =>
  resales.map((r) => ({
    value: r.id,
    label: r.company ?? `${r.firstname} ${r.lastname}`,
    object: r,
  }));


export const mapEnterprisesOptions = (enterprises: UserWithRoleSchema[]) =>
  enterprises
    .map(
      (u) =>({
          id: u.id,
          name: `${u.firstname} ${u.lastname}`,
          company: u.company,
          email: u.email,
          role: u.role.name,
          address: u.company_info?.address || u.account_user?.company_info?.address,
          type: u.company_info?.type || u.account_user?.company_info?.type,
          account_user_role: u.account_user?.role.name,
          account_user_id: u.account_user?.id,
          account_user_email: u.account_user?.email,
          account_user_name: (u.account_user?.firstname || u.account_user?.lastname ?
            `${u.account_user?.firstname || ''} ${u.account_user?.lastname || ''}` : undefined),
          account_user_company: u.account_user?.company,
        } as UserReducedSchema))
    .map((u) => ({
      value: u.id,
      label: u.account_user_company ?? u.account_user_name ?? u.company ?? u.name,
      secondaryLabel: u.email,
      address: u.address,
      type: u.type,
      object: u,
    }));

export const canUserCreateQuotation = (userScopes: string[]) => {
  return (
    (
      userScopes.includes("quotation:create_draft") &&
      userScopes.includes("quotation:create")
    ) || userScopes.includes("quotation:request")
  );
};

export const inviteExternalEnterprise = async (
  enterprise: UserReducedSchema,
  quotation: QuotationSchema,
  updateQuotation: (
    id: number,
    data: QuotationUpdateSchema,
    showNotification?: boolean
  ) => Promise<QuotationWithDraftsSchema>,
  countryCode: string,
  push: any,
  intl: any,
  setQuotation?: (q: QuotationWithDraftsSchema) => void,
  showNotification: boolean = true
) => {
  const { invite, internal, user } = await checkExternalUser(
    enterprise.secondaryLabel,
    countryCode
  );
  if (internal) {
    // CASE B: user is internal --> throw warning and exit
    push({
      type: "warning",
      title: intl.formatMessage({ id: "info_sharing_is_internal" }),
    });
    return;
  } else if (!!user) {
    /* CASE C: user is not among connected users but is registered -->
       check if user is an enterprise user and throw warning if not so,
       otherwise proceed with the invitation
     */
    if (user.role.name !== "enterprise") {
      push({
        type: "warning",
        title: intl.formatMessage({
          id: "info_not_enterprise_user",
          defaultMessage: "Utente non è un impresa",
        }),
      });
      return;
    } else {
      const updated = await updateQuotation(
        quotation.id,
        {
          ...quotation,
          [`id_enterprise` as keyof QuotationUpdateSchema]: user.id || null,
          [`des_enterprise` as keyof QuotationUpdateSchema]:
            (user.company ?? user.full_name) || null,
        },
        showNotification
      );
      if (setQuotation) setQuotation(updated);
    }
  } else if (invite) {
    // CASE D: user is not among connected users and is not registered --> invite the user
    const data = {
      email: enterprise.secondaryLabel,
      country: countryCode,
      quotation_id: quotation.id,
      role_id: 6,
    };
    await sharedApiRequest.post(`/users/invitate`, data);
    push({
      title: intl.formatMessage({
        id: "user_invited",
        defaultMessage: "Utente invitato",
      }),
      type: "success",
    });
    const updated = await updateQuotation(
      quotation.id,
      {
        ...quotation,
        [`id_enterprise` as keyof QuotationUpdateSchema]: null,
        [`des_enterprise` as keyof QuotationUpdateSchema]:
          enterprise.secondaryLabel,
      },
      showNotification
    );
    if (setQuotation) setQuotation(updated);
  } else
    throw new Error(
      `Unhandled case for external user (${{ invite, internal, user }})`
    );
};

/*
Enterprise is valid if:
- it's not selected
- it's selected and it's an option from connected user
- it's selected and it's not an option from connected user but email is valid
 */
export const isValidEnterprise = (enterprise: UserOption) =>
  !enterprise ||
  (!!enterprise &&
    // if the enterprise is an option from connected user it's ok.
    (!!enterprise.object ||
      // if the enterprise is not an option from connected user then email must be valid
      (!enterprise.object &&
        enterprise.secondaryLabel &&
        validateEmail(enterprise.secondaryLabel))));
